import * as React from 'react';

import { CardStatusTag } from '@coursera/cds-core';
import { TranslationIcon } from '@coursera/cds-icons';

import { getGenAiBadges } from 'bundles/product-card/hooks/useGenAiBadges';

import _t from 'i18n!nls/product-card';

export const BADGE_NAMES = {
  FREE: 'Free',
  FREE_TRIAL: 'Free Trial',
  PREVIEW: 'Preview',
  FULLY_TRANSLATED: 'Fully Translated',
  SUBTITLES: 'Subtitles',
  TOP_INSTRUCTOR: 'Top Instructor',
};

const isLanguageModeTag = (tagName: string): boolean => {
  return tagName === BADGE_NAMES.FULLY_TRANSLATED || tagName === BADGE_NAMES.SUBTITLES;
};

/**
 * Reorders tags to prioritize language mode labels (Fully Translated, Subtitles) first
 * @param tags - Array of React elements representing tags
 * @param tagNames - Array of tag name strings corresponding to tags
 * @returns Object with prioritized tags and tagNames arrays
 */
export const prioritizeLanguageModeTags = (
  tags: React.ReactElement[],
  tagNames: string[]
): { prioritizedTags: React.ReactElement[]; prioritizedTagNames: string[] } => {
  const languageModeTags: React.ReactElement[] = [];
  const languageModeTagNames: string[] = [];
  const otherTags: React.ReactElement[] = [];
  const otherTagNames: string[] = [];

  tags.forEach((tag, index) => {
    const tagName = tagNames[index] || '';
    if (isLanguageModeTag(tagName)) {
      languageModeTags.push(tag);
      languageModeTagNames.push(tagName);
    } else {
      otherTags.push(tag);
      otherTagNames.push(tagName);
    }
  });

  // Rebuild arrays with proper prioritization: Language Mode Labels first, then everything else
  const prioritizedTags = [...languageModeTags, ...otherTags];
  const prioritizedTagNames = [...languageModeTagNames, ...otherTagNames];

  return { prioritizedTags, prioritizedTagNames };
};

export const getProductCardStatusTags = ({
  isNew,
  statusTags,
  slug,
  hasTopInstructor,
}: {
  isNew?: boolean;
  statusTags?: string[] | null;
  slug?: string;
  hasTopInstructor?: boolean;
}) => {
  const { badges: genAiBadges, names } = getGenAiBadges(slug);
  let tags = [];
  let tagNames = [];

  if (isNew) {
    tags.push(<CardStatusTag key="new">{_t('New')}</CardStatusTag>);
    tagNames.push('New');
  }

  if (hasTopInstructor) {
    tags.push(<CardStatusTag key="top-instructor">{_t('Top Instructor')}</CardStatusTag>);
    tagNames.push('Top Instructor');
  }

  if (statusTags && statusTags.length > 0) {
    statusTags.forEach((statusTag) => {
      switch (statusTag) {
        case BADGE_NAMES.FREE:
          tags.push(<CardStatusTag key={statusTag}>{_t('Free')}</CardStatusTag>);
          tagNames.push('Free');
          break;
        case BADGE_NAMES.FREE_TRIAL:
          tags.push(<CardStatusTag key={statusTag}>{_t('Free Trial')}</CardStatusTag>);
          tagNames.push('Free Trial');
          break;
        case BADGE_NAMES.PREVIEW:
          tags.push(<CardStatusTag key={statusTag}>{_t('Preview')}</CardStatusTag>);
          tagNames.push('Preview');
          break;
        case BADGE_NAMES.FULLY_TRANSLATED:
          tags.push(
            <CardStatusTag key={statusTag} icon={<TranslationIcon />}>
              {_t('Fully Translated')}
            </CardStatusTag>
          );
          tagNames.push('Fully Translated');
          break;
        case BADGE_NAMES.SUBTITLES:
          tags.push(
            <CardStatusTag key={statusTag} icon={<TranslationIcon />}>
              {_t('Subtitles')}
            </CardStatusTag>
          );
          tagNames.push('Subtitles');
          break;
        default:
        // no ops
      }
    });

    // Prioritize language mode labels if they exist
    const hasLanguageModeLabels = statusTags.some(isLanguageModeTag);

    if (hasLanguageModeLabels) {
      const { prioritizedTags, prioritizedTagNames } = prioritizeLanguageModeTags(tags, tagNames);
      tags = prioritizedTags;
      tagNames = prioritizedTagNames;
    }
  }

  tags.push(...genAiBadges);
  tagNames.push(...names);

  return { tags, tagNames };
};
